import PasskeyIcon from '../assets/icons/PasskeyIcon';
import BitKeep from '../assets/wallets/BitKeep';
import Brave from '../assets/wallets/Brave';
import Bybit from '../assets/wallets/Bybit';
import Coin98 from '../assets/wallets/Coin98';
import Coinbase from '../assets/wallets/Coinbase';
import Frontier from '../assets/wallets/Frontier';
import ImToken from '../assets/wallets/ImToken';
import InfinityWallet from '../assets/wallets/InfinityWallet';
import Injected from '../assets/wallets/Injected';
import Ledger from '../assets/wallets/Ledger';
import MetaMask from '../assets/wallets/MetaMask';
import OKX from '../assets/wallets/OKX';
import ONTO from '../assets/wallets/ONTO';
import Onekey from '../assets/wallets/Onekey';
import Phantom from '../assets/wallets/Phantom';
import Rabby from '../assets/wallets/Rabby';
import Rainbow from '../assets/wallets/Rainbow';
import SafePal from '../assets/wallets/SafePal';
import Slope from '../assets/wallets/Slope';
import Talisman from '../assets/wallets/Talisman';
import TokenPocket from '../assets/wallets/TokenPocket';
import Trust from '../assets/wallets/Trust';
import WalletConnect from '../assets/wallets/WalletConnect';
import Zerion from '../assets/wallets/Zerion';

import React from 'react';
import { isAndroid, isIOS } from '../utils';

export type WalletConfigProps = {
  // Wallets name
  name?: string;
  // Wallets short name. Defaults to `name`
  shortName?: string;
  // Icon to display in the modal
  icon?: React.ReactNode;
  // Defaults to `'circle'`, but some icons look better as squircle (e.g. if they have a background)
  iconShape?: 'squircle' | 'circle' | 'square';
  // Defaults to `false`, but some icons don't have a background and look better if they shrink to fit the container
  iconShouldShrink?: boolean;
  // Links to download the wallet
  downloadUrls?: {
    // wallet's website
    website?: string;

    // browser extensions
    chrome?: string;
    firefox?: string;
    brave?: string;
    edge?: string;
    safari?: string;

    // app downloads
    android?: string;
    ios?: string;
  };
  // Create URI for QR code, where uri is encoded data from WalletConnect
  getWalletConnectDeeplink?: (uri: string) => string;

  // open url in Wallet's browser
  getDAppBrowserDeeplink?: (url: string) => string;
};

// Organised in alphabetical order by key
export const walletConfigs: {
  [rdns: string]: WalletConfigProps; // for multiple cases seperate rdns by comma
} = {
  'particleEVM, particleSolana, network.particle': {
    name: 'Particle Auth',
    iconShape: 'circle',
  },
  'coinbaseWallet, coinbaseWalletSDK, com.coinbase.wallet': {
    name: 'Coinbase Wallet',
    shortName: 'Coinbase',
    icon: <Coinbase />,
    iconShape: 'squircle',
    downloadUrls: {
      website: 'https://www.coinbase.com/wallet/getting-started-extension',
      chrome: 'https://chrome.google.com/webstore/detail/coinbase-wallet-extension/hnfanknocfeofbddgcijnmhnfnkdnaad',
      brave: 'https://chromewebstore.google.com/detail/coinbase-wallet-extension/hnfanknocfeofbddgcijnmhnfnkdnaad',
      android: 'https://play.google.com/store/apps/details?id=org.toshi',
      ios: 'https://apps.apple.com/app/coinbase-wallet-store-crypto/id1278383455',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return uri;
    },
    getDAppBrowserDeeplink: (uri: string) => {
      return `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(uri)}`;
    },
  },
  'metaMask, io.metamask, io.metamask.mobile': {
    name: 'MetaMask',
    icon: <MetaMask />,
    iconShouldShrink: true,
    downloadUrls: {
      website: 'https://metamask.io/download/',
      chrome: 'https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn',
      firefox: 'https://addons.mozilla.org/firefox/addon/ether-metamask/',
      brave: 'https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn',
      edge: 'https://microsoftedge.microsoft.com/addons/detail/metamask/ejbalbakoplchlghecdalmeeeajnimhm',
      android: 'https://play.google.com/store/apps/details?id=io.metamask',
      ios: 'https://apps.apple.com/app/metamask/id1438144202',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return isAndroid() || isIOS()
        ? `metamask://wc?uri=${encodeURIComponent(uri)}`
        : `https://metamask.app.link/wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  'phantom, app.phantom': {
    name: 'Phantom',
    iconShape: 'squircle',
    icon: <Phantom />,
    downloadUrls: {
      website: 'https://phantom.app/download',
      chrome: 'https://chromewebstore.google.com/detail/phantom/bfnaelmomeimhlpmgjnjophhpkkoljpa',
      brave: 'https://chromewebstore.google.com/detail/phantom/bfnaelmomeimhlpmgjnjophhpkkoljpa',
      firefox: 'https://addons.mozilla.org/en-US/firefox/addon/phantom-app/',
      android: 'https://play.google.com/store/apps/details?id=app.phantom',
      ios: 'https://apps.apple.com/us/app/phantom-crypto-wallet/id1598432977',
    },
    getDAppBrowserDeeplink: (uri: string) => {
      return `https://phantom.app/ul/browse/${encodeURIComponent(uri)}?ref=${encodeURIComponent(window.location.origin)}`;
    },
  },
  'okxWallet, oKExWallet, com.okex.wallet': {
    name: 'OKX Wallet',
    shortName: 'OKX',
    iconShape: 'squircle',
    icon: <OKX />,
    downloadUrls: {
      website: 'https://www.okx.com/download',
      chrome:
        'https://chromewebstore.google.com/detail/%E6%AC%A7%E6%98%93-web3-%E9%92%B1%E5%8C%85/mcohilncbfahbmgdjkbpemcciiolgcge',
      edge: 'https://microsoftedge.microsoft.com/addons/detail/okx-wallet/pbpjkcldjiffchgbbndmhojiacbgflha',
      brave:
        'https://chromewebstore.google.com/detail/%E6%AC%A7%E6%98%93-web3-%E9%92%B1%E5%8C%85/mcohilncbfahbmgdjkbpemcciiolgcge',
      android: 'https://play.google.com/store/apps/details?id=com.okinc.okex.gp',
      ios: 'https://apps.apple.com/us/app/okx-buy-bitcoin-btc-crypto/id1327268470',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return `okex://main/wc?uri=${encodeURIComponent(uri)}`;
    },
    getDAppBrowserDeeplink: (uri: string) => {
      const deepLink = `okx://wallet/dapp/url?dappUrl=${encodeURIComponent(uri)}`;
      return `https://www.okx.com/download?deeplink=${encodeURIComponent(deepLink)}`;
    },
  },
  'bitKeep, bitget, com.bitget.web3': {
    name: 'Bitget Wallet',
    shortName: 'Bitget',
    iconShape: 'circle',
    icon: <BitKeep />,
    downloadUrls: {
      website: 'https://web3.bitget.com/wallet-download',
      chrome: 'https://chromewebstore.google.com/detail/bitget-wallet-formerly-bi/jiidiaalihmmhddjgbnbgdfflelocpak',
      android: 'https://play.google.com/store/apps/details?id=com.bitkeep.wallet',
      ios: 'https://apps.apple.com/us/app/bitget-wallet-ex-bitkeep/id1395301115',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return `bitkeep://wc?uri=${encodeURIComponent(uri)}`;
    },
    getDAppBrowserDeeplink: (uri: string) => {
      return `https://bkcode.vip?action=dapp&url=${encodeURIComponent(uri)}`;
    },
  },
  'rabby, io.rabby': {
    name: 'Rabby Wallet',
    shortName: 'Rabby',
    icon: <Rabby />,
    downloadUrls: {
      website: 'https://rabby.io',
      chrome: 'https://chrome.google.com/webstore/detail/rabby-wallet/acmacodkjbdgmoleebolmdjonilkdbch',
    },
  },
  'rainbow, me.rainbow': {
    name: 'Rainbow Wallet',
    shortName: 'Rainbow',
    icon: <Rainbow />,
    iconShape: 'squircle',
    downloadUrls: {
      website: 'https://rainbow.me/?utm_source=connectkit',
      chrome: 'https://chrome.google.com/webstore/detail/opfgelmcmbiajamepnmloijbpoleiama',
      edge: 'https://microsoftedge.microsoft.com/addons/detail/cpojfbodiccabbabgimdeohkkpjfpbnf',
      firefox: 'https://addons.mozilla.org/firefox/addon/rainbow-extension',
      brave: 'https://rainbow.me/extension?utm_source=connectkit',
      android: 'https://play.google.com/store/apps/details?id=me.rainbow',
      ios: 'https://apps.apple.com/app/id1457119021',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return isAndroid() || isIOS()
        ? `rainbow://wc?uri=${encodeURIComponent(uri)}&connector=rainbowkit`
        : `https://rnbwapp.com/wc?uri=${encodeURIComponent(uri)}&connector=rainbowkit`;
    },
  },
  'talisman, xyz.talisman': {
    name: 'Talisman',
    shortName: 'Talisman',
    icon: <Talisman />,
    iconShape: 'squircle',
    downloadUrls: {
      website: 'https://talisman.xyz',
      chrome: 'https://chrome.google.com/webstore/detail/talisman-polkadot-wallet/fijngjgcjhjmmpcmkeiomlglpeiijkld',
      firefox: 'https://addons.mozilla.org/en-US/firefox/addon/talisman-wallet-extension/',
      brave: 'https://chromewebstore.google.com/detail/talisman-ethereum-and-pol/fijngjgcjhjmmpcmkeiomlglpeiijkld',
    },
  },
  'trust, trustWallet, com.trustwallet.app': {
    name: 'Trust Wallet',
    shortName: 'Trust',
    icon: <Trust />,
    iconShouldShrink: true,
    downloadUrls: {
      website: 'https://trustwallet.com/download',
      chrome: 'https://chromewebstore.google.com/detail/trust-wallet/egjidjbpglichdcondbcbdnbeeppgdph',
      android: 'https://play.google.com/store/apps/details?id=com.wallet.crypto.trustapp',
      ios: 'https://apps.apple.com/app/trust-crypto-bitcoin-wallet/id1288339409',
    },
    getWalletConnectDeeplink(uri) {
      return isAndroid() || isIOS()
        ? `trust://wc?uri=${encodeURIComponent(uri)}`
        : `https://link.trustwallet.com/wc?uri=${encodeURIComponent(uri)}`;
    },
    getDAppBrowserDeeplink: (uri) => {
      return `https://link.trustwallet.com/open_url?url=${encodeURIComponent(uri)}`;
    },
  },
  'com.crypto.wallet': {
    name: 'Crypto.com',
    shortName: 'Crypto',
  },
  'frontier, xyz.frontier.wallet': {
    name: 'Frontier Wallet',
    shortName: 'Frontier',
    icon: <Frontier />,
    downloadUrls: {
      website: 'https://www.frontier.xyz/download',
      chrome: 'https://chrome.google.com/webstore/detail/frontier-wallet/kppfdiipphfccemcignhifpjkapfbihd',
      brave: 'https://chromewebstore.google.com/detail/frontier-wallet/kppfdiipphfccemcignhifpjkapfbihd',
      android: 'https://play.google.com/store/apps/details?id=com.frontierwallet',
      ios: 'https://apps.apple.com/app/frontier-crypto-defi-wallet/id1482380988',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return `frontier://wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  infinityWallet: {
    name: 'Infinity Wallet',
    icon: <InfinityWallet />,
    downloadUrls: {
      website: 'https://infinitywallet.io/download',
      chrome: 'https://infinitywallet.io/download',
      firefox: 'https://infinitywallet.io/download',
      brave: 'https://infinitywallet.io/download',
      edge: 'https://infinitywallet.io/download',
    },
  },
  imToken: {
    name: 'imToken',
    icon: <ImToken />,
    downloadUrls: {
      website: 'https://token.im/download',
      android: 'https://play.google.com/store/apps/details?id=im.token.app',
      ios: 'https://itunes.apple.com/us/app/imtoken2/id1384798940',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return `imtokenv2://wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  onto: {
    name: 'ONTO',
    icon: <ONTO />,
    downloadUrls: {
      website: 'https://onto.app/en/download/',
      chrome: 'https://chromewebstore.google.com/detail/onto-wallet/ifckdpamphokdglkkdomedpdegcjhjdp',
      ios: 'https://apps.apple.com/app/onto-an-ontology-dapp/id1436009823',
      android: 'https://play.google.com/store/apps/details?id=com.github.ontio.onto',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return isAndroid() ? uri : `https://onto.app/wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  ledger: {
    name: 'Ledger Live',
    shortName: 'Ledger',
    icon: <Ledger />,
    downloadUrls: {
      website: 'https://www.ledger.com/ledger-live/download#download-device-2',
      android: 'https://play.google.com/store/apps/details?id=com.ledger.live',
      ios: 'https://apps.apple.com/app/ledger-live-web3-wallet/id1361671700',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return `ledgerlive://wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  'zerion, io.zerion.wallet': {
    name: 'Zerion',
    icon: <Zerion />,
    downloadUrls: {
      website: 'https://zerion.io/download',
      chrome: 'https://chromewebstore.google.com/detail/zerion-wallet-for-web3-nf/klghhnkeealcohjjanjjdaeeggmfmlpl',
      brave: 'https://chromewebstore.google.com/detail/zerion-wallet-for-web3-nf/klghhnkeealcohjjanjjdaeeggmfmlpl',
      ios: 'https://apps.apple.com/app/apple-store/id1456732565',
      android: 'https://play.google.com/store/apps/details?id=io.zerion.android',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return isAndroid() || isIOS()
        ? `zerion://wc?uri=${encodeURIComponent(uri)}`
        : `https://app.zerion.io/wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  slope: {
    name: 'Slope',
    icon: <Slope />,
    downloadUrls: {
      website: 'https://slope.finance/',
      chrome: 'https://chrome.google.com/webstore/detail/slope-wallet/pocmplpaccanhmnllbbkpgfliimjljgo',
      ios: 'https://apps.apple.com/app/slope-wallet/id1574624530',
      android: 'https://play.google.com/store/apps/details?id=com.wd.wallet',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return isAndroid() ? uri : `https://slope.finance/app/wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  'tokenPocket, pro.tokenpocket': {
    name: 'TokenPocket',
    icon: <TokenPocket />,
    downloadUrls: {
      website: 'https://www.tokenpocket.pro/en/download/app',
      chrome: 'https://chrome.google.com/webstore/detail/tokenpocket/mfgccjchihfkkindfppnaooecgfneiii',
      android: 'https://play.google.com/store/apps/details?id=vip.mytokenpocket',
      ios: 'https://apps.apple.com/us/app/tp-global-wallet/id6444625622',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return `tpoutside://wc?uri=${encodeURIComponent(uri)}`;
    },
  },

  'safePal, https://www.safepal.com/download': {
    name: 'SafePal',
    icon: <SafePal />,
    iconShape: 'circle',
    downloadUrls: {
      website: 'https://www.safepal.com/download',
      chrome: 'https://chrome.google.com/webstore/detail/lgmpcpglpngdoalbgeoldeajfclnhafa',
      edge: 'https://microsoftedge.microsoft.com/addons/detail/apenkfbbpmhihehmihndmmcdanacolnh',
      firefox: 'https://addons.mozilla.org/firefox/addon/safepal-extension-wallet',
      android: 'https://play.google.com/store/apps/details?id=io.safepal.wallet',
      ios: 'https://apps.apple.com/app/id1548297139',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return isAndroid() || isIOS()
        ? `safepalwallet://wc?uri=${encodeURIComponent(uri)}`
        : `https://link.safepal.io/wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  'coin98, coin98.com': {
    name: 'Coin98',
    shortName: 'Coin98',
    icon: <Coin98 />,
    iconShape: 'circle',
    downloadUrls: {
      website: 'https://coin98.com/wallet',
      chrome: 'https://chrome.google.com/webstore/detail/aeachknmefphepccionboohckonoeemg',
      android: 'https://play.google.com/store/apps/details?id=coin98.crypto.finance.media',
      ios: 'https://apps.apple.com/app/id1561969966',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return isAndroid() || isIOS()
        ? `coin98://wc?uri=${encodeURIComponent(uri)}`
        : `https://coin98.com/wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  'onekey, so.onekey.app.wallet': {
    name: 'OneKey',
    icon: <Onekey />,
    iconShape: 'squircle',
    downloadUrls: {
      website: 'https://onekey.so/download/',
      chrome: 'https://chrome.google.com/webstore/detail/jnmbobjmhlngoefaiojfljckilhhlhcj',
      android: 'https://play.google.com/store/apps/details?id=so.onekey.app.wallet',
      ios: 'https://apps.apple.com/app/id1609559473',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return isAndroid() || isIOS()
        ? `onekey-wallet://wc?uri=${encodeURIComponent(uri)}`
        : `https://app.onekey.so/wc/connect/wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  'bybitWallet, com.bybit': {
    name: 'Bybit Wallet',
    icon: <Bybit />,
    iconShape: 'squircle',
    downloadUrls: {
      website: 'https://www.bybit.com/en/download/',
      chrome: 'https://chrome.google.com/webstore/detail/pdliaogehgdbhbnmkklieghmmjkpigpa',
      android: 'https://play.google.com/store/apps/details?id=com.bybit.app',
      ios: 'https://apps.apple.com/app/id1488296980',
    },
    getWalletConnectDeeplink: (uri: string) => {
      return `bybitapp://open/route?targetUrl=by://web3/walletconnect/wc?uri=${encodeURIComponent(uri)}`;
    },
  },
  'braveWallet, com.brave.wallet': {
    name: 'Brave Wallet',
    shortName: 'Brave',
    icon: <Brave />,
    iconShape: 'squircle',
    downloadUrls: {
      website: 'https://brave.com/download/',
    },
    getDAppBrowserDeeplink: (uri) => {
      return `brave://open-url?url=${encodeURIComponent(uri)}`;
    },
  },
  'mantaWallet,manta.network': {
    name: 'Manta Wallet',
    iconShape: 'circle',
  },
  walletConnect: {
    name: 'WalletConnect',
    icon: <WalletConnect />,
    iconShape: 'square',
    getWalletConnectDeeplink: (uri: string) => uri,
  },
  injected: {
    name: 'Browser Wallet',
    shortName: 'Browser',
    icon: <Injected />,
  },
  passkeySmartWallet: {
    name: 'Passkey Smart Wallet',
    shortName: 'Passkey',
    iconShape: 'circle',
    icon: <PasskeyIcon />,
  },
} as const;

export const isCoinbase = (id: string) => {
  return ['coinbaseWallet', 'coinbaseWalletSDK', 'com.coinbase.wallet'].includes(id);
};

export const isWalletConnect = (id: string) => {
  return 'walletConnect' === id;
};

export const isPasskeySmartWallet = (id: string) => {
  return 'passkeySmartWallet' === id;
};
